import EventsEmitter from '../eventsEmitter';
import { Pool } from '../../lib/pool';
import { SlotIcon } from './SlotIcon';
import { addToGameLoop, removeFromGameLoop } from '../../lib/gameLoop';
import { iconStates } from '../../core/pixi/icons';
import { gameStore } from '../../store/gameStore';
import { isMobile } from '../../helpers/isMobile';
import { spinBarStore } from '../../store/spinBarStore';
import { soundService, SoundTypes } from '../baseSoundService/soundService';
import { winBarStore } from '../../store/winBarStore';
import { checkJackpotIconSounds, checkScattersSounds } from '../../helpers/handleIconsSounds';
import { gameMainDrawer } from '../../game/drawers/gameMainDrawer';

const START_SPEED = 0;
const STOPPING_STEPS = 10;
const START_LOOP_COUNT = 7;
const REVERSE_STEPS_STANDARD_MODE = isMobile() ? 7 : 8;
const REVERSE_STEPS_FAST_MODE = isMobile() ? 14 : 12;

export class Reel extends EventsEmitter {
  constructor(
    index,
    slotSpinContainer,
    slotWinLayer,
    params,
    iconNumberProvider,
    isLastReel,
    Icon = SlotIcon
  ) {
    super();
    Object.assign(this, params);
    this.index = index;
    this._moveSpeed = 0;
    this.initStore(index, slotSpinContainer, slotWinLayer, params, Icon);
    this._direction = 1;
    this._finalDrawIcons = [];
    this._reelIcons = [];
    this.nextIconNumber = null;
    this.isLastReel = isLastReel;
    this.iconNumberProvider = iconNumberProvider;
    this.speedFactor = 1;
  }

  initStore(index, slotSpinContainer, slotWinLayer, params, Icon) {
    const xPosition =
      (Array.isArray(isMobile() ? params.mobileColumnPositions : params.webColumnPositions)
        ? isMobile()
          ? params.mobileColumnPositions[index]
          : params.webColumnPositions[index]
        : isMobile()
        ? params.mobileColumnPositions
        : params.webColumnPositions) || 0;
    this._iconsStore = new Pool((nextNumber) => {
      return new Icon(
        {
          parent: slotSpinContainer,
          elementId: `spinIcon_${index}_${nextNumber}`,
          winParent: slotWinLayer,
          styles: { x: xPosition },
        },
        index
      );
    });
    // this._iconsStore = new Icon(
    //   {
    //     parent: slotSpinContainer,
    //     elementId: `spinIcon_${index}_${1}`,
    //     winParent: slotWinLayer,
    //     styles: { x: xPosition },
    //   },
    //   index
    // );
  }

  async startSpin(direction = 1) {
    this._direction = direction;
    this.resetReel();
    this._moveSpeed *= this._direction;
    this._spinning = true;
    addToGameLoop(this.spinLoop, this);
  }

  spinLoop(delta, x) {
    if (this._spinLoopCounter === START_LOOP_COUNT)
      this._moveSpeed = this._direction * this.columnsMoveSpeed;
    if (spinBarStore.isDoubleClick && gameStore.matrix.length) {
      this.stopSpin(true);
    }
    this._spinLoopCounter += 1;
    this.move(this._moveSpeed * (this._reversing ? 1 : x));
    if (
      !this.index &&
      !this._reversing &&
      this._moveSpeed &&
      gameMainDrawer.expandWildBar.isWildOpened
    ) {
      gameMainDrawer.expandWildBar.moveWildContainer();
    }
    this.takeOffObsoleteIcons();
    this.addIconIfNeed();
    if (this._stopping && this._finalDrawIcons.length > 0) {
      gameMainDrawer.expandWildBar.initializeExpandWildBarContainerPosition();
      // get y position of outermost final icon
      const stopIconY =
        this._direction === 1
          ? this._finalDrawIcons[this._finalDrawIcons.length - 1].y
          : this._finalDrawIcons[0].y;
      const stopRowPosition =
        this._direction === 1
          ? this.finalPositions[this.finalPositions.length - 1]
          : this.finalPositions[0];
      // not allow it to pass behind reverse line
      const reverseLine =
        this._direction === 1
          ? stopRowPosition + this.reverseLineOffset
          : stopRowPosition - this.reverseLineOffset;
      if (
        this._direction === 1 &&
        this._reversing === false &&
        reverseLine - stopIconY < this._moveSpeed
      ) {
        this._moveSpeed = reverseLine - stopIconY;
      } else if (
        this._direction === -1 &&
        this._reversing === false &&
        reverseLine - stopIconY > this._moveSpeed
      ) {
        this._moveSpeed = reverseLine - stopIconY;
      }
      // when reached reverse line set reverse move speed
      if (
        this._reversing === false &&
        ((this._direction === 1 && stopIconY >= reverseLine) ||
          (this._direction === -1 && stopIconY <= reverseLine))
      ) {
        soundService.play(SoundTypes.ReelStop);
        this._reversing = true;
        this._moveSpeed =
          -1 * this._direction * Math.abs((stopIconY - stopRowPosition) / this._reverseSteps);
      }
      if (this._reversing) {
        this._reverceLoops += 1;
        if (this._reverceLoops === this._reverseSteps) {
          this._moveSpeed = stopRowPosition - stopIconY;
        } else if (this._reverceLoops > this._reverseSteps) {
          this.finalActions();
        }
      }
    }
  }

  stopSpin(forced = false) {
    if (this._stopping === false) {
      this._stopping = true;
      this.initFromResponseData();
      this.finalDrawCardNumbers.forEach((slotIcon) => {
        checkScattersSounds(this.index, slotIcon);
        checkJackpotIconSounds(this.index, slotIcon);
        if (slotIcon === 9 && winBarStore.expandingWildData?.[this.index]) {
          soundService.play(SoundTypes.WildSymbol);
          gameMainDrawer.slotBoard.slotBoard.play('dropBoard3', false);
        }
        if (slotIcon === 11) {
          spinBarStore.setScatterStarCount(1);
        }
        if (slotIcon === 10) {
          spinBarStore.setScatterDollarCount(1);
        }
        if (slotIcon === 400) {
          spinBarStore.setJackpotIconCount(1);
        }
      });
      if (forced || spinBarStore.isDoubleClick) {
        this._reverseSteps = isMobile() ? 8 : 14;
        this.reverseLineOffset = isMobile() ? 25 : this.reverseLineOffset;
      }
      if (gameStore.deltaFrame > 1.2) {
        this._reverseSteps = Math.ceil(this._reverseSteps / gameStore.deltaFrame);
      }
    }
  }

  getRowIcon(responseRow) {
    return this._finalDrawIcons[responseRow];
  }

  fullUpdateFromResponseData() {
    this._reelIcons.forEach((i) => this._iconsStore.storeInstance(i));
    this._reelIcons.length = 0;
    this._finalDrawIcons.length = 0;
    this.initFromResponseData();
    this.finalDrawCardNumbers.forEach((cardNumber) => {
      const i = this.addToReelIcons(cardNumber, iconStates.idle, false);
      this._finalDrawIcons.push(i);
    });
    this.finalPositions.forEach((position, index) => {
      this._reelIcons[index].y = position;
    });
  }

  move(move) {
    this._reelIcons.forEach((spinIcon) => {
      spinIcon.alpha = 1;
      spinIcon.y += move;
    });
  }

  addIconIfNeed() {
    if (this.needAddIcon()) {
      const iconState = this._stopping
        ? iconStates.idle
        : this._spinning
        ? iconStates.spin
        : iconStates.idle;

      const icon = this.addToReelIcons(this.nextIconNumber, iconState, this._direction === 1);
      if (this._stopping && this.isFinalIcon) {
        this._direction === -1
          ? this._finalDrawIcons.push(icon)
          : this._finalDrawIcons.unshift(icon);
      }
    }
  }

  addToReelIcons(cardNumber, iconState, toTop = true) {
    const icon = this._iconsStore.getInstance();
    if (toTop) {
      const topIcon = this._reelIcons[0];
      if (topIcon) {
        // icon.y = topIcon.y - this.iconsHeight - this.iconsDistance;
        icon.y =
          topIcon.y - this._getIconMargin(cardNumber) - this._getIconMargin(topIcon.iconNumber);
      }
      this._reelIcons.unshift(icon);
    } else {
      const bottomIcon = this._reelIcons[this._reelIcons.length - 1];
      if (bottomIcon) {
        // icon.y = bottomIcon.y + this.iconsHeight + this.iconsDistance;
        icon.y =
          bottomIcon.y +
          this._getIconMargin(cardNumber) +
          this._getIconMargin(bottomIcon.iconNumber);
      }
      this._reelIcons.push(icon);
    }
    this.nextIconNumber = null;
    icon.show();
    icon.changeIcon(cardNumber, iconState);
    return icon;
  }

  finalActions() {
    const promises = [];
    this._finalDrawIcons.forEach((icon, index) => {
      icon.y = this.finalPositions[index];
    });
    removeFromGameLoop(this.spinLoop, this);
    Promise.all(promises).then(() => {
      this.finalTransitionsBetweenIconStores();
      this._spinning = false;
      this.emit('stopped', this);
      gameMainDrawer.expandWildBar.isEndWildContainerMove = false;
      // if (this.isLastReel) {
      //   this.emit('stopped', this);
      // }
      // gameEvents.emit('spinColumnStopped', this.index);
    });
  }

  initFromResponseData() {
    // gameStore.matrix
    // [
    //   [400, 8, 6],
    //   [400, 2, 3],
    //   [400, 4, 7],
    //   [8, 6, 6],
    //   [8, 2, 3],
    // ]
    const responseData = JSON.parse(JSON.stringify(gameStore.matrix))[this.index];
    if (!responseData) return;

    this.finalDrawCardNumbers = [...responseData];
  }

  resetReel() {
    this._stopping = false;
    this._spinLoopCounter = 0;
    this._moveSpeed = START_SPEED;
    this._reverceLoops = 0;
    this._reversing = false;
    this._brakeSteps = STOPPING_STEPS;
    this._finalDrawCardNumbers = [];
    this._finalDrawIcons = [];
    this.reverseLineOffset = isMobile() ? 15 : 35;
    this._reverseSteps = spinBarStore.gameReelSpeedMode.fast
      ? REVERSE_STEPS_FAST_MODE
      : REVERSE_STEPS_STANDARD_MODE;
    this.nextIconNumber = null;
    this.isFinalIcon = false;
  }

  needTakeOffIcon(outermostIconIndex) {
    if (outermostIconIndex === undefined) {
      outermostIconIndex = this.getOutermostIconIndex();
      if (outermostIconIndex < 0) return false;
    }
    const outermostIcon = this._reelIcons[outermostIconIndex];
    return this._direction === 1
      ? outermostIcon.y - this.iconsHeight / 2 > this.bottomLine + this.takeOffLineOffset
      : outermostIcon.y + this.iconsHeight / 2 < this.topLine - this.takeOffLineOffset;
  }

  needAddIcon() {
    if (this.nextIconNumber === null) {
      let cardNumber, finalCard;
      if (this._stopping) {
        cardNumber =
          this._direction === -1
            ? this.finalDrawCardNumbers.shift()
            : this.finalDrawCardNumbers.pop();
        // this._debug('needAddIcon STOPPING', cardNumber);
        finalCard = cardNumber !== undefined;
      }
      if (cardNumber === undefined) cardNumber = this.iconNumberProvider.getNext(this.index);
      // this._debug('needAddIcon', cardNumber);
      this.nextIconNumber = cardNumber;
      this.isFinalIcon = finalCard;
    }
    const outermostIcon =
      this._direction === 1 ? this._reelIcons[0] : this._reelIcons[this._reelIcons.length - 1];
    const distance =
      this._getIconMargin(outermostIcon.iconNumber) +
      this._getIconMargin(this.nextIconNumber) -
      this.iconsHeight / 2;
    return this._direction === 1
      ? // ? (outermostIcon.y - this.iconsHeight / 2 - this.iconsDistance) > this.topLine
        // : (outermostIcon.y + this.iconsHeight / 2 + this.iconsDistance) < this.bottomLine
        outermostIcon.y - distance > this.topLine
      : outermostIcon.y + distance < this.bottomLine;
  }

  takeOffObsoleteIcons() {
    let outermostIconIndex = this.getOutermostIconIndex();
    while (outermostIconIndex > -1 && this.needTakeOffIcon(outermostIconIndex)) {
      const icon = this._reelIcons.splice(outermostIconIndex, 1)[0];
      this._iconsStore.storeInstance(icon);
      // icon._hide();
      outermostIconIndex = this.getOutermostIconIndex();
    }
  }

  iconsToSpinContainer() {
    this._reelIcons.forEach((i) => {
      i.changeState(iconStates.idle);
      // i.toSpinParent();
    });
  }

  getOutermostIconIndex() {
    return this._direction === -1 ? (this._reelIcons.length ? 0 : -1) : this._reelIcons.length - 1;
  }

  finalTransitionsBetweenIconStores() {
    this._reelIcons = this._reelIcons.filter((icon) => {
      if (this._finalDrawIcons.indexOf(icon) === -1) {
        this._iconsStore.storeInstance(icon);
        return false;
      }
      return true;
    });
  }

  _getIconMargin(cardNumber) {
    return this.iconMargins[cardNumber.toString()] || this.iconMargins.default;
  }

  getSpecialIconIfExists(iconNumber) {
    // eslint-disable-next-line no-restricted-syntax
    for (const icon of this._reelIcons) {
      if (icon.iconNumber === iconNumber) return icon;
    }
    return null;
  }

  show() {
    this._reelIcons.forEach((i) => i.show());
  }

  hide() {
    this._reelIcons.forEach((i) => i.hide());
  }

  resetSpeed() {
    this.speedFactor = 1;
    this._iconsStore.forEach((ic) => {
      ic.setSpeedFactor(this.speedFactor);
    });
  }

  _debug(...args) {
    if (this.index === 0) {
    }
  }
}
